import {
  Column,
  ColumnFilterElementTemplateOptions,
  ColumnProps,
} from 'primereact/column';
import InputDateFilter from '@/components/InputDateFilter/InputDateFilter';
import { formatDate } from '@/utils/formatDate';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { TFunction } from 'i18next';

import {
  filterApplyTemplate,
  filterClearTemplate,
  inputTextTemplate,
} from '@/components/Table/Table';
import { DownloadPdfDocument } from '@/pages/DocumentsHistory/components/DocumentsHistoryTable/DownloadPDFDocument';
import DetailModal from './DetailModal';

export const tableColumns = (
  translates: TFunction<'AccountStatement', undefined>
): ColumnProps[] => [
  {
    field: 'selection',
    selectionMode: 'multiple',
    style: { width: '3rem' },
  },
  {
    field: 'razonSocial',
    header: translates('client'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'nombreDocumento',
    header: translates('type'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'nroDoca',
    header: translates('number'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'impMovMo',
    header: translates('balance'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'fecDoc',
    header: translates('issue'),
    body: (rowData) => formatDate(rowData.fecDoc),
    filter: true,
    showFilterMenu: true,
    sortable: true,
    filterElement: (options: ColumnFilterElementTemplateOptions) => (
      <InputDateFilter options={options} />
    ),
    filterField: 'fecDoc',
    dataType: 'date',
  },
  {
    field: 'fecVenc',
    header: translates('expiration'),
    body: (rowData) => formatDate(rowData.fecVenc),
    filter: true,
    showFilterMenu: true,
    sortable: true,
    filterElement: (options: ColumnFilterElementTemplateOptions) => (
      <InputDateFilter options={options} />
    ),
    filterField: 'fecVenc',
    dataType: 'date',
  },
  {
    field: 'antiguedad',
    header: translates('seniority'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'armador',
    header: translates('shipowner'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'contenedor',
    header: translates('container'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'viajeBuque',
    header: translates('travel'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'seleccion',
    header: translates('selection'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'referencia',
    header: translates('reference'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'referenciaLiquidacion',
    header: translates('paymentReference'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'comentarios',
    header: translates('comments'),
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'view',
    body: (rowData) => (
      <div key={rowData.oid} className="flex justify-center text-blue-800">
        <DetailModal data={rowData} />
      </div>
    ),
  },
  {
    field: 'oid',
    header: '',
    filter: false,
    showFilterMenu: false,
    sortable: false,
    headerStyle: { width: '3rem' },
    body: (rowData) => (
      <div className="flex justify-center text-blue-800">
        {rowData?.identificador && (
          <DownloadPdfDocument
            oid={rowData?.identificador}
            contenedor={rowData?.contenedor}
          />
        )}
      </div>
    ),
  },
];

export const getHeaderColumnsGroup = (extraColumn?: boolean) => (
  <ColumnGroup>
    <Row>
      <Column
        header=""
        field=""
        selectionMode="multiple"
        style={{ width: '3rem' }}
      />
      <Column
        header="Cliente"
        field="razonSocial"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        header="Tipo"
        field="nombreDocumento"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        header="Número"
        field="nroDoca"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        header="Saldo"
        field="impMovMo"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />

      <Column
        header="Emisión"
        field="fecDoc"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <InputDateFilter options={options} displayMenu />
        )}
        dataType="date"
        filterField="fecDoc"
      />
      <Column
        header="Vencimiento"
        field="fecVenc"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <InputDateFilter options={options} displayMenu />
        )}
        dataType="date"
        filterField="fecVenc"
      />
      <Column
        header="Antiguedad"
        field="antiguedad"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />

      <Column
        header="Armador"
        field="armador"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        header="Contenedor"
        field="contenedor"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />

      <Column
        header="Viaje"
        field="viajeBuque"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />

      <Column
        header="Selección"
        field="seleccion"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        header="Referencia"
        field="referencia"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        header="Ref de liquidación"
        field="referenciaLiquidacion"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        header="Comentarios"
        field="comentarios"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />

      {extraColumn && (
        <Column
          header="Retención"
          field="montoRetencion"
          sortable
          showFilterMenu
          filter
          showFilterMatchModes={false}
          filterApply={filterApplyTemplate}
          filterClear={filterClearTemplate}
          filterElement={inputTextTemplate}
        />
      )}
      <Column field="view" style={{ width: 50 }} />
      <Column field="oid" style={{ width: 50 }} />
    </Row>
  </ColumnGroup>
);
